<template>
  <div v-if="isAdmin || isModerator" class="space-y-6">
    <!-- Section Header -->
    <div class="flex items-center justify-between">
      <h2 class="text-xl font-black tracking-tight">
        {{ $t("analytics_overview") || "Analytics Overview" }}
      </h2>
      <span
        v-if="loading"
        class="text-xs text-slate-400 font-bold animate-pulse"
        >Loading...</span
      >
    </div>

    <!-- Error -->
    <div
      v-if="error"
      class="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-4 rounded-xl"
    >
      {{ error }}
    </div>

    <template v-if="!loading && analytics">
      <!-- Revenue KPI Row -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div
          class="bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm"
        >
          <p
            class="text-xs font-black text-slate-400 uppercase tracking-wider mb-1"
          >
            Revenue This Month
          </p>
          <p class="text-2xl font-black text-primary">
            {{ formatCurrency(analytics.revenue_this_month) }}
          </p>
        </div>
        <div
          class="bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm"
        >
          <p
            class="text-xs font-black text-slate-400 uppercase tracking-wider mb-1"
          >
            Revenue Last Month
          </p>
          <p class="text-2xl font-black text-slate-700 dark:text-slate-200">
            {{ formatCurrency(analytics.revenue_last_month) }}
          </p>
        </div>
        <div
          class="bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm"
        >
          <p
            class="text-xs font-black text-slate-400 uppercase tracking-wider mb-1"
          >
            Revenue Trend
          </p>
          <p
            class="text-2xl font-black"
            :class="
              analytics.revenue_trend > 0
                ? 'text-green-500'
                : analytics.revenue_trend < 0
                ? 'text-red-500'
                : 'text-slate-400'
            "
          >
            <span v-if="analytics.revenue_trend !== null">
              {{ analytics.revenue_trend > 0 ? "+" : ""
              }}{{ analytics.revenue_trend }}%
            </span>
            <span v-else class="text-slate-400 text-base font-bold"
              >No data</span
            >
          </p>
        </div>
      </div>

      <!-- Charts Row 1: Monthly Bookings + Revenue -->
      <div class="grid lg:grid-cols-2 gap-6">
        <div
          class="bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm"
        >
          <p class="text-sm font-black text-slate-700 dark:text-slate-200 mb-4">
            Monthly Bookings
          </p>
          <canvas ref="bookingsChart" height="200"></canvas>
        </div>
        <div
          class="bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm"
        >
          <p class="text-sm font-black text-slate-700 dark:text-slate-200 mb-4">
            Monthly Revenue (EGP)
          </p>
          <canvas ref="revenueChart" height="200"></canvas>
        </div>
      </div>

      <!-- Charts Row 2: Top Cars + Top Owners -->
      <div class="grid lg:grid-cols-2 gap-6">
        <div
          class="bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm"
        >
          <p class="text-sm font-black text-slate-700 dark:text-slate-200 mb-4">
            Top Cars by Bookings
          </p>
          <canvas ref="topCarsChart" height="220"></canvas>
        </div>
        <div
          class="bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm"
        >
          <p class="text-sm font-black text-slate-700 dark:text-slate-200 mb-4">
            Top Owners by Revenue
          </p>
          <canvas ref="topOwnersChart" height="220"></canvas>
        </div>
      </div>

      <!-- Charts Row 3: Booking Status + Users by Role -->
      <div class="grid lg:grid-cols-2 gap-6">
        <div
          class="bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm"
        >
          <p class="text-sm font-black text-slate-700 dark:text-slate-200 mb-4">
            Booking Status Distribution
          </p>
          <div class="flex items-center justify-center">
            <canvas ref="bookingStatusChart" height="220" width="220"></canvas>
          </div>
        </div>
        <div
          class="bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm"
        >
          <p class="text-sm font-black text-slate-700 dark:text-slate-200 mb-4">
            Users by Role
          </p>
          <div class="flex items-center justify-center">
            <canvas ref="userRoleChart" height="220" width="220"></canvas>
          </div>
        </div>
      </div>

      <!-- Charts Row 4: Monthly New Users + Cancellation Rate -->
      <div class="grid lg:grid-cols-3 gap-6">
        <div
          class="lg:col-span-2 bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm"
        >
          <p class="text-sm font-black text-slate-700 dark:text-slate-200 mb-4">
            New Users per Month
          </p>
          <canvas ref="newUsersChart" height="180"></canvas>
        </div>
        <div
          class="bg-white dark:bg-dark-surface rounded-2xl p-5 border border-slate-100 dark:border-white/5 shadow-sm flex flex-col items-center justify-center text-center"
        >
          <p
            class="text-xs font-black text-slate-400 uppercase tracking-wider mb-3"
          >
            Cancellation Rate
          </p>
          <p
            class="text-5xl font-black"
            :class="
              analytics.cancellation_rate > 30
                ? 'text-red-500'
                : analytics.cancellation_rate > 15
                ? 'text-amber-500'
                : 'text-green-500'
            "
          >
            {{ analytics.cancellation_rate }}%
          </p>
          <p class="text-xs text-slate-400 mt-2">of all bookings cancelled</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  Chart,
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  BarElement,
  ArcElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

Chart.register(
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  BarElement,
  ArcElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
);

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default {
  name: "DashboardAnalytics",

  props: {
    analytics: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    isAdmin: { type: Boolean, default: false },
    isModerator: { type: Boolean, default: false },
    bookingStats: { type: Object, default: () => ({}) },
  },

  data() {
    return { charts: {} };
  },

  watch: {
    analytics(val) {
      if (val) this.$nextTick(() => this.buildCharts());
    },
  },

  beforeUnmount() {
    Object.values(this.charts).forEach((c) => c?.destroy());
  },

  methods: {
    formatCurrency(val) {
      return new Intl.NumberFormat("en-EG", {
        style: "currency",
        currency: "EGP",
        maximumFractionDigits: 0,
      }).format(val || 0);
    },

    monthLabel(year, month) {
      return `${MONTH_NAMES[month - 1]} ${year}`;
    },

    destroyAll() {
      Object.values(this.charts).forEach((c) => c?.destroy());
      this.charts = {};
    },

    buildCharts() {
      this.destroyAll();
      const a = this.analytics;

      // ── Shared palette ──────────────────────────────────────────────
      const primary = "#6366f1";
      const green = "#22c55e";
      const amber = "#f59e0b";
      const red = "#ef4444";
      const blue = "#3b82f6";
      const purple = "#a855f7";
      const barColors = [
        primary,
        green,
        amber,
        red,
        blue,
        purple,
        "#14b8a6",
        "#f97316",
        "#ec4899",
        "#8b5cf6",
      ];

      const monthly = a.monthly_bookings_revenue || [];
      const monthLabels = monthly.map((m) => this.monthLabel(m.year, m.month));

      // ── Monthly Bookings ─────────────────────────────────────────────
      this.charts.bookings = new Chart(this.$refs.bookingsChart, {
        type: "bar", // ← غيّر من line لـ bar
        data: {
          labels: monthLabels.length ? monthLabels : ["No data yet"],
          datasets: [
            {
              label: "Bookings",
              data: monthly.length ? monthly.map((m) => m.total_bookings) : [0],
              backgroundColor: primary,
              borderRadius: 8,
            },
          ],
        },
        options: this.barOpts(),
      });

      // ── Monthly Revenue ──────────────────────────────────────────────
      this.charts.revenue = new Chart(this.$refs.revenueChart, {
        type: "bar", // ← غيّر من line لـ bar
        data: {
          labels: monthLabels.length ? monthLabels : ["No data yet"],
          datasets: [
            {
              label: "Revenue (EGP)",
              data: monthly.length
                ? monthly.map((m) => parseFloat(m.revenue) || 0)
                : [0],
              backgroundColor: green,
              borderRadius: 8,
            },
          ],
        },
        options: this.barOpts(),
      });

      // ── Top Cars ─────────────────────────────────────────────────────
      const cars = a.top_cars || [];
      this.charts.topCars = new Chart(this.$refs.topCarsChart, {
        type: "bar",
        data: {
          labels: cars.map((c) => c.model_name || `Car #${c.car_id}`),
          datasets: [
            {
              label: "Bookings",
              data: cars.map((c) => c.bookings_count),
              backgroundColor: barColors.slice(0, cars.length),
              borderRadius: 6,
            },
          ],
        },
        options: this.barOpts(),
      });

      // ── Top Owners ───────────────────────────────────────────────────
      const owners = a.top_owners || [];
      this.charts.topOwners = new Chart(this.$refs.topOwnersChart, {
        type: "bar",
        data: {
          labels: owners.map((o) => o.username || `Owner #${o.owner_id}`),
          datasets: [
            {
              label: "Revenue (EGP)",
              data: owners.map((o) => parseFloat(o.revenue) || 0),
              backgroundColor: barColors.slice(0, owners.length),
              borderRadius: 6,
            },
          ],
        },
        options: this.barOpts(),
      });

      // ── Booking Status Pie ───────────────────────────────────────────
      const bs = this.bookingStats || {};
      this.charts.bookingStatus = new Chart(this.$refs.bookingStatusChart, {
        type: "doughnut",
        data: {
          labels: [
            "Completed",
            "Pending",
            "Confirmed",
            "Cancelled",
            "Rejected",
          ],
          datasets: [
            {
              data: [
                bs.completed || 0,
                bs.pending || 0,
                bs.confirmed || 0,
                bs.cancelled || 0,
                bs.rejected || 0,
              ],
              backgroundColor: [green, amber, blue, red, purple],
              borderWidth: 0,
            },
          ],
        },
        options: this.doughnutOpts(),
      });

      // ── Users by Role Pie ────────────────────────────────────────────
      const ur = a.users_by_role || {};
      this.charts.userRole = new Chart(this.$refs.userRoleChart, {
        type: "doughnut",
        data: {
          labels: [
            "Renters",
            "Individual Owners",
            "Org Owners",
            "Admins",
            "Moderators",
          ],
          datasets: [
            {
              data: [
                ur.renters || 0,
                ur.individual_owners || 0,
                ur.organization_owners || 0,
                ur.admins || 0,
                ur.moderators || 0,
              ],
              backgroundColor: [blue, green, primary, red, amber],
              borderWidth: 0,
            },
          ],
        },
        options: this.doughnutOpts(),
      });

      // ── New Users per Month ──────────────────────────────────────────
      const mu = a.monthly_users || [];
      this.charts.newUsers = new Chart(this.$refs.newUsersChart, {
        type: "bar",
        data: {
          labels: mu.map((m) => this.monthLabel(m.year, m.month)),
          datasets: [
            {
              label: "New Users",
              data: mu.map((m) => m.count),
              backgroundColor: primary,
              borderRadius: 6,
            },
          ],
        },
        options: this.barOpts(),
      });
    },

    lineOpts() {
      return {
        responsive: true,
        plugins: { legend: { display: false }, tooltip: { mode: "index" } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { weight: "bold" } } },
          y: { grid: { color: "#f1f5f9" }, beginAtZero: true },
        },
      };
    },

    barOpts() {
      return {
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: { mode: "index" },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { weight: "bold", size: 12 } },
          },
          y: {
            grid: { color: "#f1f5f9" },
            beginAtZero: true,
            ticks: { font: { weight: "bold" } },
          },
        },
      };
    },

    doughnutOpts() {
      return {
        responsive: false,
        plugins: {
          legend: {
            position: "bottom",
            labels: { font: { weight: "bold" }, padding: 12 },
          },
        },
        cutout: "65%",
      };
    },
  },
};
</script>
